import { format, isValid, parseISO } from "date-fns";

/** Length of the leading `yyyy-MM-dd` portion of an ISO datetime. */
const CALENDAR_DATE_LENGTH = 10;

/**
 * Parses only the calendar-date portion of an ISO datetime string, ignoring any time or UTC offset.
 *
 * Use this for dates that are meant to be read exactly as they were entered — an occurrence's
 * start/end date, for example — so the displayed day never shifts with the viewer's timezone.
 * Returns undefined when the input is absent or unparseable.
 *
 * This is the helper new code should reach for. `formatDate` in `@/mixins/helpers` reaches the same
 * result by slicing the string by hand, and still backs many older call sites.
 */
export const parseCalendarDate = (dateTimeString?: string | null): Date | undefined => {
  if (!dateTimeString || dateTimeString.length < CALENDAR_DATE_LENGTH) {
    return undefined;
  }

  const parsedDate = parseISO(dateTimeString.slice(0, CALENDAR_DATE_LENGTH));

  if (!isValid(parsedDate)) {
    return undefined;
  }

  return parsedDate;
};

/**
 * Formats the calendar-date portion of an ISO datetime string for display, ignoring any time or
 * UTC offset. Returns an empty string when the input is absent or unparseable.
 *
 * Use this when the date should read exactly as it was entered. Use `formatDateUtc` from
 * `@/util/date` instead when the UTC instant is what matters — it converts before formatting, so a
 * date-only string or a time near midnight can render a different day.
 *
 * `dateFormat` is deliberately required: with no default, changing this file cannot alter how any
 * screen already renders its dates.
 */
export const formatCalendarDate = (dateTimeString: string | null | undefined, dateFormat: string): string => {
  const parsedDate = parseCalendarDate(dateTimeString);

  if (!parsedDate) {
    return "";
  }

  return format(parsedDate, dateFormat);
};
